/* eslint-disable react/no-unescaped-entities */
import { useEffect } from "react";
import { ShieldCheck, Radar, Zap, Globe, Award, ArrowRight, Sparkles } from "lucide-react";

const PERKS = [
  { icon: Radar, text: "Every donation form checked every hour — even on weekends and holidays" },
  { icon: Zap, text: "Instant alert emails the moment anything breaks" },
  { icon: Globe, text: "Whole-site coverage — up to 150 pages, not just the homepage" },
  { icon: Award, text: "Public verification badge unlocked for your website" },
];

export default function UpgradeSuccess({ open, onClose }) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center px-4 py-8 bg-black/60 backdrop-blur-md animate-in fade-in duration-300"
      role="dialog" aria-modal="true" onClick={onClose} data-testid="upgrade-success-modal">
      <div className="relative w-full max-w-lg bg-white rounded-3xl shadow-premium overflow-hidden max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}>
        {/* Hero: dark, glowing radar */}
        <div className="bg-charcoal-900 text-white px-7 pt-10 pb-8 text-center relative overflow-hidden">
          {/* Ambient glow */}
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute -top-24 left-1/2 -translate-x-1/2 w-72 h-72 rounded-full bg-emerald-500/20 blur-3xl animate-pulse" />
            <div className="absolute top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2 w-40 h-40 rounded-full border border-emerald-400/20" />
            <div className="absolute top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2 w-60 h-60 rounded-full border border-emerald-400/10" />
            <div className="absolute top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2 w-80 h-80 rounded-full border border-emerald-400/5" />
          </div>

          {/* Central shield with radar pulse */}
          <div className="relative inline-flex items-center justify-center mb-5">
            <span className="absolute inset-0 rounded-full bg-emerald-500/30 animate-ping" style={{ animationDuration: "2s" }} />
            <div className="relative w-20 h-20 rounded-full bg-emerald-500/15 border border-emerald-400/40 grid place-items-center">
              <ShieldCheck className="w-9 h-9 text-emerald-400" strokeWidth={2} />
            </div>
          </div>

          <div className="relative">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/15 border border-emerald-500/25 mb-4">
              <Sparkles className="w-3 h-3 text-emerald-300" />
              <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-emerald-300">
                You're on Protection
              </p>
            </div>
            <h2 className="text-[26px] sm:text-[30px] font-semibold tracking-tight leading-tight">
              Mission Watch Protection is <span className="text-emerald-400">now ON.</span>
            </h2>
            <p className="mt-3 text-sm text-white/60 leading-relaxed max-w-md mx-auto">
              You can breathe. We're watching your website, donation page, forms, and security
              around the clock — and we'll email you the moment anything breaks.
            </p>
          </div>
        </div>

        {/* Perks */}
        <div className="px-7 py-6 space-y-3.5" data-testid="upgrade-success-perks">
          {PERKS.map((p, i) => {
            const Icon = p.icon;
            return (
              <div
                key={p.text}
                className="flex items-start gap-3 animate-in fade-in slide-in-from-bottom-2"
                style={{ animationDelay: `${i * 90 + 200}ms`, animationDuration: "500ms", animationFillMode: "backwards" }}
              >
                <div className="w-8 h-8 rounded-lg shrink-0 grid place-items-center bg-emerald-50 text-emerald-600">
                  <Icon className="w-4 h-4" strokeWidth={2.2} />
                </div>
                <p className="text-sm text-charcoal-800 leading-relaxed pt-1">{p.text}</p>
              </div>
            );
          })}
        </div>

        {/* CTA */}
        <div className="border-t border-black/5 px-7 py-5 bg-charcoal-50/50">
          <button
            onClick={onClose}
            className="w-full h-12 rounded-xl bg-charcoal-900 text-white text-[15px] font-semibold hover:bg-charcoal-800 transition-colors inline-flex items-center justify-center gap-2"
            data-testid="upgrade-success-cta"
          >
            Take me to my dashboard <ArrowRight className="w-4 h-4" />
          </button>
          <p className="mt-3 text-[11px] font-mono text-charcoal-400 text-center leading-relaxed">
            Mission Watch Protection begins protecting your critical pages within 60 minutes.
          </p>
        </div>
      </div>
    </div>
  );
}
